import { Transition, TransitionStatus } from "react-transition-group";
import React, { CSSProperties, useRef } from "react";
import styled from "styled-components";
import { palette } from "@shared/EDS/ns-color-palette";
import { KNOP_TAB_BAR_HEIGHT } from "../layoutConstants";

export interface SidePanelProps extends React.PropsWithChildren {
  readonly title: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly animationTimeInMs?: number;
}
export function SidePanel({
  title,
  isOpen,
  onClose,
  animationTimeInMs = 300,
  children,
}: Readonly<SidePanelProps>): React.JSX.Element {
  const panelRef = useRef(null);
  return (
    <Transition in={isOpen} timeout={{ exit: animationTimeInMs }} nodeRef={panelRef}>
      {(state) => (
        <StyledSidePanel
          ref={panelRef}
          className={isOpen ? "open" : "closed"}
          style={transitionStyling(state, animationTimeInMs)}
        >
          <Header>
            <h3>{title}</h3>
            <tractie-button
              data-testid="side-panel-close-button"
              level="tertiary"
              onClick={onClose}
              icon-only={true}
            >
              <tractie-icon name="close" />
            </tractie-button>
          </Header>
          <ScrollableContent>{children}</ScrollableContent>
        </StyledSidePanel>
      )}
    </Transition>
  );
}

function transitionStyling(
  transitionStatus: TransitionStatus,
  animationTimeInMs: number,
): CSSProperties {
  const transitionStyles: Record<TransitionStatus, CSSProperties> = {
    unmounted: {},
    entering: { transform: "translateX(100%)" },
    entered: { transform: "translateX(0)" },
    exiting: {},
    exited: { display: "none" },
  };
  return {
    transition: `transform ${animationTimeInMs}ms ease-in-out`,
    transform: "translateX(100%)",
    visibility: "visible",
    ...transitionStyles[transitionStatus],
  };
}

const ScrollableContent = styled.div`
  overflow-y: auto;
  height: calc(100% - ${KNOP_TAB_BAR_HEIGHT}px);
`;

const StyledSidePanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.backgroundElevated1};
  border-left: ${({ theme }) => `1px solid ${theme.colors.border}`};
  z-index: 1000;
  padding: 20px;
  box-shadow: 0 8px 20px 0 ${palette.alphaBlack50};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.border}`};

  h3 {
    margin: 0;
    font-weight: var(--tractie-typography-weight-medium);
  }
`;
